// Reports — 真實資料版（Route 5, PR #95）。
//
// 後端 endpoint 既有：
//   GET /v1/reports?kind=daily — 列出今天的所有 active site 日報
//   GET /v1/reports/:reportId  — 細節 + R2 download_url（report-api 走 daily
//                                rollup cron；PDF / CSV 由 report job 觸發
//                                時上傳到 R2，未產生時這張回 download_url=null）
//
// 此 page 主畫面是日報 list + preview drawer。Monthly / weekly / custom 報表
// 由後端 cron + report job 產生；目前只 daily kind 有 cron 產出，其他 kind
// 走 list 會回空 → UI 顯示中性 EmptyState。

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FileText,
  Eye,
  RefreshCw,
  ServerCrash,
  Inbox,
  Building2,
  Download,
} from "lucide-react";
import PageHeader from "../components/PageHeader.js";
import DataTable, { type Column } from "../components/DataTable.js";
import EmptyState from "../components/EmptyState.js";
import TenantPickerEmptyState from "../components/TenantPickerEmptyState.js";
import Skeleton from "../components/Skeleton.js";
import Drawer from "../components/Drawer.js";
import {
  listReports,
  getReport,
  friendlyReportsError,
  type ReportSummary,
  type ReportKind,
  type ReportDetailResponse,
} from "../lib/reportsApi.js";
import { useDashboardSession } from "../auth/useDashboardSession.js";

const KIND_LABEL: Record<ReportKind, string> = {
  daily: "日報",
  weekly: "週報",
  monthly: "月報",
  custom: "自訂",
};
const KIND_BADGE: Record<ReportKind, string> = {
  daily: "badge-default",
  weekly: "badge-default",
  monthly: "badge-accent",
  custom: "badge-warning",
};
const STATUS_BADGE: Record<ReportSummary["status"], string> = {
  ready: "badge-success",
  queued: "badge-warning",
  failed: "badge-danger",
};
const STATUS_LABEL: Record<ReportSummary["status"], string> = {
  ready: "可閱覽",
  queued: "排程中",
  failed: "失敗",
};

type ListState =
  | { status: "loading" }
  | { status: "ready"; reports: ReportSummary[] }
  | { status: "error"; message: string };

export default function Reports(): React.ReactElement {
  const session = useDashboardSession();
  const tenantParam = session.super_admin ? session.acting_tenant_id ?? null : null;
  const [kind, setKind] = useState<ReportKind>("daily");
  const [state, setState] = useState<ListState>({ status: "loading" });
  const [open, setOpen] = useState<ReportSummary | null>(null);

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const r = await listReports(kind, tenantParam);
      setState({ status: "ready", reports: r.reports });
    } catch (err) {
      setState({ status: "error", message: friendlyReportsError(err) });
    }
  }, [kind, tenantParam]);

  useEffect(() => { void load(); }, [load]);

  const columns: Column<ReportSummary>[] = useMemo(() => [
    {
      key: "name", header: "報表名稱", primary: true, width: "40%",
      render: (r) => (
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-text-muted flex-shrink-0" />
          <span className="font-medium text-text-primary">{r.title}</span>
        </div>
      ),
    },
    {
      key: "kind", header: "類型",
      render: (r) => <span className={KIND_BADGE[r.kind]}>{KIND_LABEL[r.kind]}</span>,
    },
    {
      key: "scope", header: "範圍", hideOnMobile: true,
      render: (r) => <span className="text-text-secondary">{r.scope_label}</span>,
    },
    {
      key: "generated", header: "產生時間", hideOnMobile: true,
      render: (r) => <span className="font-mono text-text-secondary">{r.date_local} {r.generated_at_local}</span>,
    },
    {
      key: "status", header: "狀態",
      render: (r) => <span className={STATUS_BADGE[r.status]}>{STATUS_LABEL[r.status]}</span>,
    },
    {
      key: "action", header: "", width: "1%",
      render: (r) => (
        <button className="btn-ghost text-xs h-7" onClick={(e) => { e.stopPropagation(); setOpen(r); }} disabled={r.status !== "ready"}>
          <Eye className="h-3.5 w-3.5" />
          檢視
        </button>
      ),
    },
  ], []);

  if (session.super_admin && !tenantParam) {
    return (
      <>
        <PageHeader title="報表中心" description="案場日報、月報與彙整匯出" />
        <TenantPickerEmptyState />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="報表中心"
        description="每天凌晨自動產生前一天的案場日報；週報、月報與自訂匯出由報表管理員手動觸發"
        actions={
          <button className="btn-secondary text-sm" onClick={() => void load()} disabled={state.status === "loading"}>
            <RefreshCw className={`h-4 w-4 ${state.status === "loading" ? "animate-spin" : ""}`} /> 重新整理
          </button>
        }
      />
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        {(["daily", "weekly", "monthly", "custom"] as ReportKind[]).map((k) => (
          <button
            key={k}
            onClick={() => setKind(k)}
            className={`text-xs rounded-md px-3 py-1.5 border transition-colors ${
              kind === k
                ? "bg-accent-subtle border-accent/40 text-accent"
                : "bg-bg-surface border-border text-text-secondary hover:bg-bg-hover hover:text-text-primary"
            }`}
          >
            {KIND_LABEL[k]}
          </button>
        ))}
      </div>

      {state.status === "loading" && <ReportsSkeleton />}
      {state.status === "error" && (
        <EmptyState icon={ServerCrash} title="報表列表載入失敗" description={state.message}
          action={<button className="btn-primary text-sm" onClick={() => void load()}>再試一次</button>} />
      )}
      {state.status === "ready" && state.reports.length === 0 && (
        kind === "daily" ? (
          <EmptyState icon={Inbox} title="今天還沒有可閱覽的報表" description="每天凌晨自動產生前一天的日報；當案場開始有巡邏紀錄後，隔天就會出現在這裡。" />
        ) : (
          <EmptyState
            icon={Inbox}
            title={`沒有可閱覽的${KIND_LABEL[kind]}`}
            description={`目前只有日報會自動彙整。如需${KIND_LABEL[kind]}，由報表管理員手動觸發產製作業建立後會出現於此清單。`}
          />
        )
      )}
      {state.status === "ready" && state.reports.length > 0 && (
        <DataTable
          rows={state.reports}
          columns={columns}
          rowKey={(r) => r.report_id}
          searchableText={(r) => `${r.title} ${r.scope_label}`}
          onRowClick={(r) => setOpen(r)}
        />
      )}

      <Drawer
        open={!!open}
        onClose={() => setOpen(null)}
        title={open?.title ?? "報表"}
        description={open ? `${open.scope_label} · ${open.date_local}` : undefined}
        size="lg"
      >
        {open && <ReportPreview report={open} tenantId={tenantParam} />}
      </Drawer>
    </>
  );
}

function ReportPreview({ report, tenantId }: { report: ReportSummary; tenantId: string | null }): React.ReactElement {
  const [data, setData] = useState<ReportDetailResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getReport(report.report_id, tenantId)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setErr(friendlyReportsError(e)); });
    return () => { cancelled = true; };
  }, [report.report_id, tenantId]);

  if (err) return <p className="text-sm text-danger">{err}</p>;
  if (!data) {
    return <div className="space-y-2"><Skeleton className="h-4 w-1/2" /><Skeleton className="h-3 w-full" /><Skeleton className="h-3 w-full" /></div>;
  }
  return (
    <div className="space-y-4 text-sm">
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded border border-border bg-bg-raised p-3">
          <p className="text-xs text-text-secondary">日期</p>
          <p className="mt-0.5 font-mono text-text-primary">{data.date_local}</p>
        </div>
        <div className="rounded border border-border bg-bg-raised p-3">
          <p className="text-xs text-text-secondary">產生時間</p>
          <p className="mt-0.5 font-mono text-text-primary">{data.generated_at_local}</p>
        </div>
      </div>
      {data.preview_sections?.map((sec, idx) => (
        <div key={idx} className="rounded border border-border bg-bg-raised p-3">
          <p className="text-xs font-semibold text-text-primary">{sec.title}</p>
          <ul className="mt-2 space-y-1 text-text-secondary">
            {sec.lines.map((l, i) => (<li key={i} className="font-mono text-xs">{l}</li>))}
          </ul>
        </div>
      ))}
      {data.summary && Object.keys(data.summary).length > 0 && (
        <div className="rounded border border-border bg-bg-raised p-3">
          <p className="text-xs font-semibold text-text-primary mb-2">摘要</p>
          <pre className="text-2xs text-text-secondary whitespace-pre-wrap">{JSON.stringify(data.summary, null, 2)}</pre>
        </div>
      )}
      {data.download_url ? (
        <a href={data.download_url} className="btn-primary text-sm w-full sm:w-auto inline-flex items-center justify-center" target="_blank" rel="noreferrer">
          <Download className="h-4 w-4" /> 下載完整報表
        </a>
      ) : (
        <p className="text-xs text-text-muted">這張報表還沒產生 PDF / CSV 匯出檔；目前只提供線上摘要。</p>
      )}
    </div>
  );
}

function ReportsSkeleton(): React.ReactElement {
  return (
    <div className="card overflow-hidden">
      <div className="p-4 border-b border-border"><Skeleton className="h-9 w-72" /></div>
      <div className="divide-y divide-border">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center gap-4 p-4">
            <Skeleton className="h-4 w-4 rounded" />
            <Skeleton className="flex-1 h-4" />
            <Skeleton className="h-5 w-12" />
            <Skeleton className="h-5 w-16 hidden sm:block" />
          </div>
        ))}
      </div>
    </div>
  );
}
